import { css } from '@/styled-system/css';

export const immersiveFilterNavSection = css({
  md: {
    width: '452px !important',
  },
  xxl: {
    width: '880px !important',
  },
});

export const immersiveHeader = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
  paddingX: '2x',
  backgroundColor: 'hpxWhite',
  flexShrink: 0,
});

export const immersiveHeaderCounter = css({
  fontSize: 'sm',
  fontWeight: '600',
  color: 'hpxBlue600',
});

export const immersiveHeaderButton = css({
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  color: 'hpxBlue600',
  _hover: {
    backgroundColor: 'hpxGrey100',
  },
});

export const immersiveScrollContainer = css({
  flex: 1,
  overflowY: 'scroll',
  overscrollBehavior: 'contain',
  scrollSnapType: 'y mandatory',
  WebkitOverflowScrolling: 'touch',
  display: 'flex',
  flexDirection: 'column',
  gap: '2x',
  outline: 'none',
});

export const immersiveScrollPage = css({
  height: 'calc(100% - 40px)',
  flexShrink: 0,
  scrollSnapAlign: 'start',
  scrollSnapStop: 'always',
});

export const immersiveCard = css({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: 'hpxBlack',
  marginX: '1x',
  marginBottom: 'calc(token(spacing.1x) + env(safe-area-inset-bottom))',

  md: {
    marginX: '2x',
    marginBottom: '2x',
  },
});

export const immersiveRightActions = css({
  position: 'absolute',
  bottom: '3x',
  right: '3x',
  display: 'flex',
  flexDirection: 'column',
  gap: '2x',
  zIndex: 'zIndexOverParent',
});

export const immersiveActionWrapper = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

export const immersiveActionLabel = css({
  color: 'hpxWhite',
  fontSize: 'tiny',
  fontWeight: 'bold',
  marginTop: 'xs',
});

export const immersiveActionButton = css({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: 'hpxWhite',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  fontSize: 'md',
  color: 'hpxTeal500',
});

export const immersiveSocialButtonOverride = css({
  '& button': {
    color: 'hpxTeal500',
  },
});

export const immersiveImageContainer = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  backgroundColor: 'hpxGrey100',
});

export const immersiveImage = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: 0,
  transitionProperty: 'opacity',
  transitionDuration: '0.15s',
  transitionTimingFunction: 'ease-in',
});

export const immersiveImageLoaded = css({
  opacity: 1,
});

/**
 * Two-phase pan animation for the active immersive card's cover image.
 * Only applied when the image has loaded AND the card is currently visible
 * (controlled via isActive prop in ImmersiveCard).
 *
 * Phase 1 — Initial half-pan (one-shot, 10s):
 *   Keyframe:  slowPanInit          — object-position 50% → 60%
 *   Timing:    10s ease-out forwards — decelerates into the right edge
 *
 * Phase 2 — Continuous loop (starts after Phase 1, infinite):
 *   Keyframe:  slowPanLoop          — 60% → hold → 40% → hold
 *   Timing:    21.5s ease-in-out infinite alternate
 *   The `alternate` direction reverses each cycle, so a single set of
 *   keyframes handles both the right→left and left→right pans.
 *   `ease-in-out` naturally decelerates into both edges.
 *   Flat keyframes at 0-7% and 93-100% add ~1.5s holds at each extreme.
 *
 * Because Phase 1 starts at 50% center (the default object-position),
 * there is no visual snap when the class is applied.
 *
 * All keyframes and tokens are defined in panda.config.ts.
 */
export const immersiveImagePanning = css({
  animation: 'slowPanning',
});

export const immersiveBottomOverlay = css({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
  color: 'hpxWhite',
  paddingTop: '80px',
  paddingX: '3x',
  paddingBottom: '3x',
});

export const immersivePropertyName = css({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  textAlign: 'center',
  fontSize: 'lg',
  fontWeight: 'bold',
  color: 'hpxWhite',
  padding: '3x',
  paddingTop: '22px',
  lineHeight: '1.2',
  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
  zIndex: 'zIndexStep',
});

export const immersiveLocationText = css({
  fontSize: 'sm',
  opacity: 0.9,
  marginBottom: '1x',
});

export const immersivePriceContainer = css({
  marginBottom: '1x',
});

export const immersivePriceDisplay = css({
  fontSize: '28px',
  fontWeight: 'bold',
  marginBottom: 'xs',
  lineHeight: '1.2',
});

export const immersivePriceCheck = css({
  display: 'flex',
  alignItems: 'center',
  gap: '1x',
  fontSize: 'sm',
  fontWeight: 'bold',
  opacity: 0.9,
});

export const immersiveCheckIcon = css({
  display: 'flex',
  flexShrink: 0,
});

export const immersiveDetailsText = css({
  fontSize: 'sm',
  opacity: 0.9,
  marginBottom: '2x',
});

export const immersiveExpandButton = css({
  backgroundColor: 'transparent',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'rgba(255,255,255,0.5)',
  color: 'hpxWhite',
  paddingX: '3x',
  paddingY: '1x',
  borderRadius: '4px',
  fontSize: 'sm',
  fontWeight: '600',
  lineHeight: '24px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1x',
  textDecoration: 'none',
  transitionProperty: 'all',
  transitionDuration: '0.2s',
  _hover: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderColor: 'hpxWhite',
  },
});

export const immersiveExpandIcon = css({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  transform: 'translateY(-1px)',
});

export const immersiveCardPlaceholder = css({
  height: '100%',
  backgroundColor: 'hpxGrey100',
  borderRadius: '12px',
  marginX: '1x',
  marginBottom: 'calc(token(spacing.1x) + env(safe-area-inset-bottom))',
  md: {
    marginX: '2x',
    marginBottom: '2x',
  },
});

export const immersiveLoadingIndicator = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '3x',
  color: 'hpxGrey600',
  fontSize: 'sm',
  flexShrink: 0,
});

export const immersiveMiniMapTrigger = css({
  position: 'absolute',
  top: '32px',
  right: '12px',
  zIndex: 'zIndexOverParent',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  width: '54px',
  height: '54px',
  '& > div': {
    width: '100%',
    height: '100%',
  },
});
